package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.SettingsPageUIState
import com.example.mystybag.common.logic.BSNavigatorLanguageSelection
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_APP_LANGUAGE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_THEME_CODE
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_DARK
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_LIGHT
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_SYSTEM
import com.example.mystybag.core.expected.LocaleHelper
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.getLanguage
import com.example.mystybag.core.expected.openNotificationsSettings
import com.example.mystybag.core.expected.openSettings
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.appCore.logic.NavigatorMain
import com.example.mystybag.appCore.logic.NavigatorSelectPage
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_DELIVERY
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_FAVORITES
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_HOME
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_PROFILE
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.AppViewModel
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.lib.setup.MystyBagLibSDK
import com.example.mystybag.permissionsUtils.expected.Permission
import com.example.mystybag.permissionsUtils.expected.PermissionHelper
import com.example.mystybag.sharedmodels.ModelsData
import kotlinx.coroutines.launch

class SettingsPageViewModel(context: AppContext) : ChildViewModel<SettingsPageUIState>(
    context = context,
    uiData = SettingsPageUIState(
        languageDisplayValue = "",
        categoryDisplayValue = sharedString(SR.strings.value),
        selectedThemeCode = Preferences.getInt(PREFERENCES_THEME_CODE, 0)
    )
) {

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.settings)
        val lang = getLanguage()
        updateState {
            it.copy(
                languageDisplayValue = languageCodeToDisplayName(lang),
                currentLanguageCode = lang
            )
        }
        refreshPermissionStates()
    }

    override fun onResume() {
        refreshPermissionStates()
    }

    fun onBackClick() {
        goBack()
    }

    fun onThemeSelected(themeCode: Int) {
        Preferences.setInt(PREFERENCES_THEME_CODE, themeCode)
        val isDark = when (themeCode) {
            THEME_CODE_DARK -> true
            THEME_CODE_SYSTEM -> MystyBagLibSDK.isDarkMode ?: false
            else -> false
        }
        AppViewModel.setDarkTheme(isDark)
        updateState { it.copy(selectedThemeCode = themeCode) }
    }

    fun onLanguageClick() {
        parentOpenBottomSheet(
            BSNavigatorLanguageSelection(
                currentLanguageCode = getLanguage(),
                onLanguageSelected = { code ->
                    parentCloseBottomSheet()
                    changeLanguage(code)
                }
            )
        )
    }

    fun changeLanguage(language: String) {
        Preferences.setString(PREFERENCES_APP_LANGUAGE, language)
        ModelsData.language = language
        LocaleHelper.updateLocaleAndShortcuts(language)
        // Restart app so the new locale is applied everywhere
        scope.launch {
            setResult<MainCallback> {
                popToMainAndNavigate(NavigatorMain)
            }
        }
    }

    fun onCategoryClick() {
        // TODO: open category picker
    }

    fun onLocationPermissionClick() {
        if (uiState.value.isLocationPermissionGranted) {
            openSettings()
            return
        }

        scope.launch {
            PermissionHelper.requestPermission(Permission.Location)
            refreshPermissionStates()
        }
    }

    fun onNotificationPermissionClick() {
        if (uiState.value.isNotificationPermissionGranted) {
            openNotificationsSettings()
            return
        }

        scope.launch {
            val granted = PermissionHelper.requestPermission(Permission.Notification)
            refreshPermissionStates()
            if (!granted && !PermissionHelper.hasPermission(Permission.Notification)) {
                // iOS does not show the prompt again after first deny.
                // Navigate user to app settings so they can enable notifications manually.
                openNotificationsSettings()
            }
        }
    }

    private fun refreshPermissionStates() {
        updateState {
            it.copy(
                isLocationPermissionGranted = PermissionHelper.hasPermission(Permission.Location),
                isNotificationPermissionGranted = PermissionHelper.hasPermission(Permission.Notification)
            )
        }
    }

    private fun languageCodeToDisplayName(code: String): String = when (code) {
        "en" -> sharedString(SR.strings.english)
        "bg" -> sharedString(SR.strings.bulgarian)
        else -> code
    }
}
